import { FormEvent, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import useFarmers from "../../services/useFarmers";
import useCreateFarmer from "../../services/useCreateFarmer";
import useUpdateFarmer from "../../services/useUpdateFarmer";
import useDeleteFarmer from "../../services/useDeleteFarmer";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";

const Farmers = () => {
  const { data: farmers, isLoading, isError } = useFarmers();
  const { mutate: createFarmer, isLoading: isSubmitting } = useCreateFarmer();
  const { mutate: updateFarmer, isLoading: isUpdating } = useUpdateFarmer();
  const { mutate: deleteFarmer, isLoading: isDeleting } = useDeleteFarmer();
  const [name, setName] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [editingFarmerId, setEditingFarmerId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState("");

  const sortedFarmers = useMemo(() => {
    return [...(farmers ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }, [farmers]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedName = name.trim();
    if (!trimmedName) {
      setErrorMessage("Informe o nome da agricultora.");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");

    createFarmer(
      { data: { name: trimmedName } },
      {
        onSuccess: () => {
          setName("");
          setSuccessMessage("Agricultora criada com sucesso.");
        },
        onError: (error: any) => {
          const message =
            error?.name?.[0] ||
            error?.detail ||
            "Nao foi possivel criar a agricultora.";
          setErrorMessage(message);
        },
      },
    );
  };

  const handleStartEdit = (farmerId: number, farmerName: string) => {
    setEditingFarmerId(farmerId);
    setEditingName(farmerName);
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleCancelEdit = () => {
    setEditingFarmerId(null);
    setEditingName("");
  };

  const handleSaveEdit = (farmerId: number) => {
    const trimmedName = editingName.trim();
    if (!trimmedName) {
      setErrorMessage("Informe o nome da agricultora.");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");

    updateFarmer(
      { farmerId, data: { name: trimmedName } },
      {
        onSuccess: () => {
          setEditingFarmerId(null);
          setEditingName("");
          setSuccessMessage("Nome da agricultora atualizado com sucesso.");
        },
        onError: (error: any) => {
          const message =
            error?.name?.[0] ||
            error?.detail ||
            "Nao foi possivel atualizar a agricultora.";
          setErrorMessage(message);
        },
      },
    );
  };

  const handleDelete = (farmerId: number) => {
    setErrorMessage("");
    setSuccessMessage("");

    deleteFarmer(
      { farmerId },
      {
        onSuccess: () => {
          if (editingFarmerId === farmerId) {
            setEditingFarmerId(null);
            setEditingName("");
          }
          setSuccessMessage("Agricultora removida com sucesso.");
        },
        onError: (error: any) => {
          const message =
            error?.detail || "Nao foi possivel remover a agricultora.";
          setErrorMessage(message);
        },
      },
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography component="h1" variant="h4">
            Agricultoras
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Cadastre novas agricultoras e acompanhe as ja vinculadas a esta
            semeadora.
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Box component="form" onSubmit={handleSubmit}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  autoFocus
                  fullWidth
                  label="Nome da agricultora"
                  onChange={(event) => {
                    setName(event.target.value);
                  }}
                  value={name}
                />
                <Button
                  disabled={isSubmitting}
                  sx={{ minWidth: { sm: 180 } }}
                  type="submit"
                  variant="contained"
                >
                  {isSubmitting ? "Salvando..." : "Criar agricultora"}
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {successMessage ? (
          <Alert
            onClose={() => {
              setSuccessMessage("");
            }}
            severity="success"
          >
            {successMessage}
          </Alert>
        ) : null}

        {errorMessage ? (
          <Alert
            onClose={() => {
              setErrorMessage("");
            }}
            severity="error"
          >
            {errorMessage}
          </Alert>
        ) : null}

        <Card>
          <CardContent>
            <Typography component="h2" sx={{ mb: 2 }} variant="h6">
              Agricultoras cadastradas
            </Typography>

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : null}

            {!isLoading && isError ? (
              <Alert severity="error">
                Nao foi possivel carregar a lista de agricultoras.
              </Alert>
            ) : null}

            {!isLoading && !isError && sortedFarmers.length === 0 ? (
              <Typography color="text.secondary">
                Nenhuma agricultora cadastrada ainda.
              </Typography>
            ) : null}

            {!isLoading && !isError && sortedFarmers.length > 0 ? (
              <List disablePadding>
                {sortedFarmers.map((farmer) => (
                  <ListItem
                    divider
                    key={farmer.id}
                    disableGutters
                    secondaryAction={
                      <Stack direction="row" spacing={1}>
                        {editingFarmerId === farmer.id ? (
                          <>
                            <IconButton
                              aria-label="Salvar agricultora"
                              color="primary"
                              disabled={isUpdating}
                              onClick={() => {
                                handleSaveEdit(farmer.id);
                              }}
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton
                              aria-label="Cancelar edicao"
                              disabled={isUpdating}
                              onClick={handleCancelEdit}
                            >
                              <CloseIcon />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton
                            aria-label="Editar agricultora"
                            disabled={isDeleting}
                            onClick={() => {
                              handleStartEdit(farmer.id, farmer.name);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        <IconButton
                          aria-label="Remover agricultora"
                          color="error"
                          disabled={
                            !farmer.can_delete || isDeleting || isUpdating
                          }
                          onClick={() => {
                            handleDelete(farmer.id);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    }
                  >
                    {editingFarmerId === farmer.id ? (
                      <TextField
                        fullWidth
                        label="Nome da agricultora"
                        onChange={(event) => {
                          setEditingName(event.target.value);
                        }}
                        size="small"
                        value={editingName}
                      />
                    ) : (
                      <ListItemText
                        primary={farmer.name}
                        secondary={
                          farmer.products_count > 0
                            ? `Vinculada a ${farmer.products_count} produto(s)`
                            : "Sem produtos vinculados"
                        }
                        sx={{ pr: 16 }}
                      />
                    )}
                  </ListItem>
                ))}
              </List>
            ) : null}
          </CardContent>
        </Card>
      </Stack>
    </Container>
  );
};

export default Farmers;
